import type { StripeElements } from '@stripe/stripe-js';
import { useStripe } from '@/providers/StripeProvider';
import { createSetupIntent } from '@/lib/api/stripe';
import { useRecaptcha } from '@/hooks/useRecaptcha';
import { handleApiError } from '@/lib/utils/apiErrorHandler';
import { stanNotify } from '@/lib/stanNotify';

/**
 * Hook to handle express checkout confirmation flow
 * Confirms the setup intent after user completes Google Pay/Apple Pay/Link
 */
export const useExpressCheckout = () => {
    const stripe = useStripe();
    const { getToken, isReady: recaptchaReady } = useRecaptcha();

    const confirmExpressCheckout = async (elements: StripeElements, customerId: string): Promise<string | null> => {
        if (!stripe) {
            stanNotify({
                type: 'error',
                title: 'Stripe not loaded',
                text: 'Please wait for Stripe to load and try again.',
            });
            return null;
        }

        if (!recaptchaReady) {
            stanNotify({
                type: 'error',
                title: 'Something went wrong',
                text: 'We were unable to validate your payment method. Please try again in a few seconds.',
            });
            return null;
        }

        try {
            const token = await getToken('CreateSetupIntent');
            const secret = await createSetupIntent(customerId, token);

            const result = await stripe.confirmSetup({
                elements,
                clientSecret: secret,
                redirect: 'if_required',
            });

            if (result.error) {
                stanNotify({
                    type: 'error',
                    title: 'There was an issue',
                    text: result.error.message || 'We were unable to validate your payment method.',
                });
                console.error(result.error);
                return null;
            }

            return result.setupIntent.payment_method as string;
        } catch (error) {
            handleApiError(error, 'Payment Error');
            return null;
        }
    };

    return { confirmExpressCheckout };
};
